import { agents } from '../data/agents';
import { blandFunnel30, blandLoOutcomes } from '../data/blandOutcomes';
import { rangeFactor } from './metrics';
import type { Agent, BlandFunnel, BlandPoint, DateRange, Team, VolumePoint } from '../types/voip';

export interface BlandLoRow {
  agent: Agent;
  transfers: number;
  opportunities: number;
  /** Opportunities ÷ transfers received */
  conversion: number;
  rank: number;
}

export interface BlandReportData {
  funnel: BlandFunnel;
  rows: BlandLoRow[];
  series: BlandPoint[];
}

const totalTransfers30 = blandLoOutcomes.reduce((s, o) => s + o.transfers, 0);

export function buildBlandReport(range: DateRange, team: Team | 'All', base: VolumePoint[]): BlandReportData {
  const f = rangeFactor[range];
  const scoped = agents
    .filter((a) => (team === 'All' || a.team === team) && blandLoOutcomes.some((o) => o.agentId === a.id))
    .map((agent) => {
      const o = blandLoOutcomes.find((x) => x.agentId === agent.id) ?? { agentId: agent.id, transfers: 0, opportunities: 0 };
      return { agent, raw: o.transfers, transfers: Math.round(o.transfers * f), opportunities: Math.round(o.opportunities * f) };
    });

  const transferred = scoped.reduce((s, r) => s + r.transfers, 0);
  const opportunities = scoped.reduce((s, r) => s + r.opportunities, 0);
  const teamShare = scoped.reduce((s, r) => s + r.raw, 0) / totalTransfers30;

  const funnel: BlandFunnel = {
    dials: Math.round(blandFunnel30.dials * f * teamShare),
    pickedUp: Math.round(blandFunnel30.pickedUp * f * teamShare),
    transferred,
    opportunities,
  };

  const rows = [...scoped]
    .sort((a, b) => b.opportunities - a.opportunities || b.transfers - a.transfers)
    .map((r, i) => ({
      agent: r.agent,
      transfers: r.transfers,
      opportunities: r.opportunities,
      conversion: r.transfers === 0 ? 0 : r.opportunities / r.transfers,
      rank: i + 1,
    }));

  const weights = base.map((p) => p.outbound + p.inbound + p.missed);
  const weightSum = weights.reduce((s, w) => s + w, 0) || 1;
  const series = base.map((p, i) => ({
    label: p.label,
    transfers: Math.round((transferred * weights[i]) / weightSum),
    opportunities: Math.round((opportunities * weights[i]) / weightSum),
  }));

  return { funnel, rows, series };
}
